import { boardTokens } from "@/shared/theme/tokens";

export const radialOffsets = (column: number, row: number, count: number) =>
  Array.from({ length: count }, (_, index) => {
    if (index === 0) return { x: 0, y: 0 };
    const hiddenCount = Math.max(1, count - 1);
    const angle = hiddenCount === 1 ? 45 : 6 + ((index - 1) * 78) / (hiddenCount - 1);
    const radians = (angle * Math.PI) / 180;
    const radius = 42 + Math.max(0, count - 2) * 10;
    return {
      x: Math.round(Math.cos(radians) * radius) * (column < 4 ? 1 : -1),
      y: Math.round(Math.sin(radians) * radius) * (row < 4 ? 1 : -1),
    };
  });
export type EvidenceSlot = "critical" | "motif" | "principle" | "verified";
export type DisclosureCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export const hasCollapsedEvidenceStack = (slots: EvidenceSlot[]) => new Set(slots).size < slots.length;

export const circularRadialLayout = (count: number, radius: number, startAngle = -45) =>
  Array.from({ length: count }, (_, index) => {
    const angle = startAngle + (index * 360) / Math.max(1, count);
    const radians = (angle * Math.PI) / 180;
    return { angle, x: Math.cos(radians) * radius, y: Math.sin(radians) * radius };
  });

export const boundedCircularRadialLayout = (
  count: number,
  radius: number,
  centerX: number,
  centerY: number,
  boardSize: number,
  badgeSize: number,
) => {
  const points = circularRadialLayout(count, radius);
  if (points.length === 0) return points;
  const halfBadge = badgeSize / 2;
  const minX = Math.min(...points.map(({ x }) => centerX + x - halfBadge));
  const maxX = Math.max(...points.map(({ x }) => centerX + x + halfBadge));
  const minY = Math.min(...points.map(({ y }) => centerY + y - halfBadge));
  const maxY = Math.max(...points.map(({ y }) => centerY + y + halfBadge));
  const shiftX = minX < 0 ? -minX : maxX > boardSize ? boardSize - maxX : 0;
  const shiftY = minY < 0 ? -minY : maxY > boardSize ? boardSize - maxY : 0;
  return points.map((point) => ({ ...point, x: point.x + shiftX, y: point.y + shiftY }));
};
export const evidenceRadius = (squareSize: number, count = 1) =>
  Math.max(38, squareSize / 2 + 19, count > 1 ? 34 / (2 * Math.sin(Math.PI / count)) : 0);
export const evidenceBadgeSize = (squareSize: number) => Math.min(boardTokens.badgeSize, squareSize * 0.4);
export const collapsedEvidenceOffset = (slot: EvidenceSlot, squareSize: number, stackIndex = 0, stackCount = 1) => {
  const badgeSize = evidenceBadgeSize(squareSize);
  const corner = squareSize / 2 - badgeSize / 2 - Math.min(4, (squareSize - 2 * badgeSize) / 4);
  if (slot === "motif") {
    const offset = squareSize / 2 - badgeSize / 2 + squareSize * 0.08;
    const stack = stackIndex * Math.min(4, Math.max(0, offset - badgeSize / 2 - 2) / Math.max(1, stackCount - 1));
    return { x: offset - stack, y: -offset + stack };
  }
  if (slot === "critical") return { x: -corner, y: -corner };
  if (slot === "verified") return { x: corner, y: corner };
  const stack = stackIndex * Math.min(4, Math.max(0, corner - badgeSize / 2 - 2) / Math.max(1, stackCount - 1));
  return { x: -corner + stack, y: corner - stack };
};

export const needsEvidenceWheel = (slots: EvidenceSlot[], squareSize: number, badgeSize = boardTokens.badgeSize) => {
  if (hasCollapsedEvidenceStack(slots)) return true;
  const offsets = slots.map((slot, index) => {
    const slotIndex = slots.slice(0, index).filter((candidate) => candidate === slot).length;
    const slotCount = slots.filter((candidate) => candidate === slot).length;
    return collapsedEvidenceOffset(slot, squareSize, slotIndex, slotCount);
  });
  return offsets.some((point, index) =>
    offsets
      .slice(index + 1)
      .some((other) => Math.abs(point.x - other.x) < badgeSize && Math.abs(point.y - other.y) < badgeSize),
  );
};

type BoardRect = Pick<DOMRect, "left" | "top" | "width" | "height">;

export const squareAtBoardPoint = (
  rect: BoardRect,
  clientX: number,
  clientY: number,
  orientation: "white" | "black",
): string | null => {
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) return null;
  const column = Math.floor(x / (rect.width / 8));
  const row = Math.floor(y / (rect.height / 8));
  const file = orientation === "white" ? column : 7 - column;
  const rank = orientation === "white" ? 8 - row : row + 1;
  return `${String.fromCharCode(97 + file)}${rank}`;
};

export const disclosureCorner = (column: number, row: number): DisclosureCorner =>
  `${row < 4 ? "bottom" : "top"}-${column < 4 ? "right" : "left"}` as DisclosureCorner;

export const disclosureNeedsExternalDock = (boardSize: number, column: number, row: number, count: number) => {
  if (count < 2) return false;
  const corner = disclosureCorner(column, row);
  const { inset, minWidth, maxWidth, minHeight, maxHeight } = boardTokens.evidenceDisclosure;
  const width = Math.min(maxWidth, Math.max(minWidth, boardSize / 2 - inset * 2));
  const height = Math.min(maxHeight, Math.max(minHeight, boardSize / 2 - inset * 2));
  const left = corner.endsWith("left") ? inset : boardSize - width - inset;
  const top = corner.startsWith("top") ? inset : boardSize - height - inset;
  const centerX = ((column + 0.5) * boardSize) / 8;
  const centerY = ((row + 0.5) * boardSize) / 8;
  const radius = evidenceRadius(boardSize / 8, count);
  return boundedCircularRadialLayout(count, radius, centerX, centerY, boardSize, boardTokens.badgeSize).some(
    ({ x, y }) => {
      const badgeLeft = centerX + x - 15;
      const badgeTop = centerY + y - 15;
      return badgeLeft < left + width && badgeLeft + 30 > left && badgeTop < top + height && badgeTop + 30 > top;
    },
  );
};
